import EmptyStateServerError from "@/components/empty-state/EmptyStateServerError.tsx";
import { Search } from "@/components/search/Search.tsx";
import { useDebounceUpdate } from "@/hooks/use-debounce-update.jook.ts";
import { EmployeeSalaryTableV2 } from "@/pages/reporting/employee/salary-v2/components/EmployeeSalaryTableV2.tsx";
import { useReportingDetails } from "@/queries/reporting/use-reporting.query.ts";
import {
  ApiReportingDetailsFormat,
  ApiReportingDetailsInput,
  ApiReportingDetailsSortDirection,
  ApiReportingDetailsSortField,
} from "@/types/reporting/reporting.types.ts";
import { Page, PaginationBlock, TableSorting, TableSortingDirection } from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { CompensationRollupType } from "@/types/analytics/analytics.employee.types.ts";

interface EmployeeSalaryReportTableV2Props {
  input: ApiReportingDetailsInput;
  enabled: boolean;
  rollupType: CompensationRollupType;
}

const SORT_FIELDS = new Set<string>(Object.values(ApiReportingDetailsSortField));

export function EmployeeSalaryReportTableV2({
  input,
  enabled,
  rollupType,
}: EmployeeSalaryReportTableV2Props) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") ?? "");

  const sortFieldParam = searchParams.get("sort_field");
  const sortDirectionParam = searchParams.get("sort_direction");
  const sortField =
    sortFieldParam && SORT_FIELDS.has(sortFieldParam)
      ? (sortFieldParam as ApiReportingDetailsSortField)
      : ApiReportingDetailsSortField.employee_name;
  const sortDirection =
    sortDirectionParam === ApiReportingDetailsSortDirection.desc
      ? ApiReportingDetailsSortDirection.desc
      : ApiReportingDetailsSortDirection.asc;
  const tableSortDirection =
    sortDirection === ApiReportingDetailsSortDirection.desc
      ? TableSortingDirection.desc
      : TableSortingDirection.asc;

  const sorting = useMemo<TableSorting>(
    () => ({ fields: [{ field: sortField, direction: tableSortDirection }] }),
    [tableSortDirection, sortField]
  );

  const detailsInput = useMemo<ApiReportingDetailsInput>(
    () => ({
      ...input,
      page_number: Number(searchParams.get("page")) || 1,
      text: searchParams.get("search") || undefined,
      sorting: [{ field: sortField, direction: sortDirection }],
      format: ApiReportingDetailsFormat.page,
    }),
    [input, searchParams, sortDirection, sortField]
  );

  const details = useReportingDetails(detailsInput, enabled);

  useDebounceUpdate({
    data: searchTerm,
    timeout: 300,
    execute: (value) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (value) next.set("search", value);
        else next.delete("search");
        next.set("page", "1");
        return next;
      });
    },
  });

  const handlePageChange = (page: number) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("page", page.toString());
      return next;
    });
  };

  const handleSortingChange = (nextSorting: TableSorting) => {
    const nextField = nextSorting.fields[0];
    if (!nextField || !SORT_FIELDS.has(nextField.field)) return;

    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("sort_field", nextField.field);
      next.set("sort_direction", nextField.direction);
      next.set("page", "1");
      return next;
    });
  };

  return (
    <Page.StickyLayout
      toolbar={
        <Search
          onInput={(event) => setSearchTerm(event.currentTarget.value)}
          type="text"
          value={searchTerm}
          placeholder={t("common:search")}
          clearable
          onClear={() => setSearchTerm("")}
        />
      }
      footer={
        details.data?.metadata ? (
          <PaginationBlock metadata={details.data.metadata} onPageChange={handlePageChange} />
        ) : undefined
      }
    >
      {details.isError ? (
        <EmptyStateServerError />
      ) : (
        <EmployeeSalaryTableV2
          data={details.data?.items ?? []}
          isLoading={details.isLoading}
          sorting={sorting}
          onSortUpdated={handleSortingChange}
          rollupType={rollupType}
        />
      )}
    </Page.StickyLayout>
  );
}
